<template>
  <AppDialog
    :density="DIALOG_DENSITY.COMPACT"
    :show="show"
    :size="350"
    :title="$t('reactions.reactions')"
    hide-footer
    hide-hero
    style="
      --dialog-content-wrapper-padding: 0;
      --dialog-content-padding-left: 0;
      --dialog-content-padding-right: 0;
    "
    @on-close="handleClose"
  >
    <div class="urm-Content">
      <div class="urm-ReactionList">
        <AppButtonNext
          v-for="reaction in reactions"
          :key="reaction.reactionCode"
          :class="{ 'urm-Reaction-selected': isSelected(reaction) }"
          :variant="BUTTON_VARIANTS.SECONDARY_GRAY"
          class="urm-Reaction"
          remove-padding
          @click="handleReactionSelect(reaction)"
        >
          {{ decodeEmoji(reaction.reactionCode) }}
          <span class="urm-ReactionCount">{{ getReactionCount(reaction) }}</span>
        </AppButtonNext>
      </div>

      <div ref="users-list" class="urm-UsersList">
        <div v-for="user in getSelectedReaction.users" :key="user.accountId" class="urm-UserItem">
          <AppAvatar :avatar-url="user.avatarUrl" no-margins />
          <span class="urm-UserName oboard-truncated-text">{{ user.userName }}</span>
        </div>
      </div>
    </div>
  </AppDialog>
</template>

<script setup>
import { computed, nextTick, useTemplateRef } from 'vue'

import { getReactionCount } from '@/utils/comments'
import { BUTTON_VARIANTS } from '@/utils/components-configurations/app-button'
import { DIALOG_DENSITY } from '@/utils/components-configurations/app-dialog'
import { decodeEmoji } from '@/utils/emoji'

import AppDialog from '@/components/AppDialog'
import AppAvatar from '@/components/ui/AppAvatar/AppAvatar'
import AppButtonNext from '@/components/ui/AppButtonNext/AppButtonNext'

defineOptions({
  name: 'UserReactionsModal'
})

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },

  reactions: {
    type: Array,
    required: true
  },

  selectedReactionCode: {
    type: String,
    default: ''
  }
})

const emit = defineEmits({
  'update:show': null,
  'reaction-select': null
})

const getSelectedReaction = computed(() => {
  return (
    props.reactions.find(reaction => reaction.reactionCode === props.selectedReactionCode) || {}
  )
})

const isSelected = reaction => {
  return reaction.reactionCode === props.selectedReactionCode
}

const handleClose = () => {
  emit('update:show', false)
}

const usersListRef = useTemplateRef('users-list')
const handleReactionSelect = reaction => {
  emit('reaction-select', reaction)

  nextTick(() => {
    if (usersListRef.value) {
      usersListRef.value.scrollTop = 0
    }
  })
}
</script>

<style lang="scss" scoped>
.urm-Content {
  display: flex;
  height: 294px;
  border-bottom-right-radius: $border-radius-sm-next;
  overflow: hidden;
}

.urm-ReactionList {
  display: flex;
  flex-direction: column;
  background: var(--gray-blue-0);
  padding: 20px;
  gap: 10px;
  border-radius: 0 0 0 10px;
}

.urm-Reaction {
  height: 24px;
  font-weight: fw('bold');
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 6px;
  border-radius: $border-radius-sm-next;
  transition: background-color $transition-fast;
  --abn-background: var(--white-color);
  color: $dark-2;
  font-size: $fs-14;
  gap: 0;

  &:hover {
    background-color: $grey-2-next;
  }

  &-selected {
    box-shadow: 0 0 0 4px var(--gray-100);
  }
}

.urm-ReactionCount {
  font-size: $fs-14;
  color: $dark-3;
  margin-left: 4px;
}

.urm-UsersList {
  width: 100%;
  overflow-y: auto;
}

.urm-UserItem {
  display: flex;
  align-items: center;
  padding: 10px;
  transition: background-color $transition-fast;

  &:not(:last-child) {
    border-bottom: 1px solid var(--gray-blue-0);
  }

  &:hover {
    background-color: $grey-3-next;
  }
}

.urm-UserName {
  font-size: $fs-14;
  line-height: 20px;
  color: var(--gray-800);
  margin-left: 8px;
}
</style>
